import { create } from "zustand";
import type { Msg } from "@interlucent/organization-protocol";
import { applyMessage } from "./messaging";
import type { StoreState } from "./messaging";
import { logger } from './log';

const log = logger('store');

/**
 * Extended state type that includes the apply and reset methods.
 */
type State = StoreState & {
  apply: (m: Msg) => void;
  reset: () => void;
  adminOrgOverride: string | null;
  setAdminOrgOverride: (orgId: string | null) => void;
};

const initialState: Omit<StoreState, never> = {
  lastSeq: 0,
  rowVersion: 0,
  organization: null,
  projects: {},
  profiles: {},
  applications: {},
  versions: {},
  mappings: {},
  appPoolCoverage: {},
  uploadProgress: {},
  customHostnames: {},
  snapshotLoading: false,
  chunksReceived: 0,
  chunksExpected: 0,
  loadedTypes: new Set<string>(),
  loadedApps: new Set<string>(),
  connectionState: 'disconnected' as const,
  wsEverConnected: false,
};

export const useStore = create<State>((set, get) => ({
  ...initialState,
  adminOrgOverride: null,

  reset: () => set({
    ...initialState,
    loadedTypes: new Set<string>(),
    loadedApps: new Set<string>(),
  }),

  apply: (m) => {
    log.debug(`apply() called: ${m.kind} ${m.type} seq: ${m.seq} lastSeq: ${get().lastSeq}`);
    applyMessage(m, get, set);
  },

  setAdminOrgOverride: (orgId) => set({ adminOrgOverride: orgId }),
}));

// ============================================================================
// HELPER HOOKS FOR LOADING STATE
// ============================================================================

/**
 * Check if a data type has been loaded from the snapshot
 */
export function useIsLoaded(type: 'organization' | 'projects' | 'profiles' | 'uploads') {
  return useStore(s => s.loadedTypes.has(type));
}

/**
 * Check if a specific application has been loaded from the snapshot
 */
export function useIsAppLoaded(appId: string) {
  return useStore(s => s.loadedApps.has(appId));
}

/**
 * Check if snapshot streaming is in progress
 */
export function useIsSnapshotLoading() {
  return useStore(s => s.snapshotLoading);
}

/**
 * Get snapshot loading progress (received/expected chunks)
 */
export function useSnapshotProgress() {
  return useStore(s => ({
    received: s.chunksReceived,
    expected: s.chunksExpected,
    percent: s.chunksExpected > 0 ? Math.round((s.chunksReceived / s.chunksExpected) * 100) : 0
  }));
}
